import React, { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  FolderKanban,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Search,
  ListTodo,
  PlayCircle,
  CheckCircle2,
  User,
  Users,
  FolderOpen,
  FolderCheck,
  ClipboardList,
  Shield,
  UsersRound,
  BookOpen,
  CircleDot,
  Briefcase,
  Layers,
  Flag,
  ListChecks,
} from 'lucide-react';
import { t } from '@/i18n/ro';

interface SubNavItem {
  id: string;
  label: string;
  path?: string;
  children?: SubNavItem[];
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: SubNavItem[];
  adminOnly?: boolean; // Doar pentru administratori
}

const navItems: NavItem[] = [
  {
    id: 'central-dashboard',
    label: 'Panou central',
    icon: <BarChart3 className="w-5 h-5" />,
    path: '/panou-central',
  },
  // Lista obiectivelor de investiții anexată la buget (Anexa 89). Stă lângă
  // Panou central, nu sub Proiecte: un obiectiv de investiții e o linie de
  // buget, iar 125 din 152 nu au proiect în platformă.
  {
    id: 'lista-investitii',
    label: 'Lista de investiții',
    icon: <ListChecks className="w-5 h-5" />,
    path: '/lista-investitii',
  },
  // Căutarea în dosarele investițiilor. Fără intrarea asta, pagina /cautare se
  // poate atinge doar prin overlay (Ctrl+K) sau scriind adresa — adică nu există
  // pentru cine folosește aplicația cu mouse-ul.
  {
    id: 'cautare',
    label: t('nav.search'),
    icon: <Search className="w-5 h-5" />,
    path: '/cautare',
  },
  {
    id: 'activities',
    label: 'Activități',
    icon: <ClipboardList className="w-5 h-5" />,
    children: [
      { id: 'my-activities', label: 'Activitățile mele', path: '/' },
      { id: 'todo', label: 'De făcut', path: '/activitati/de-facut' },
      { id: 'in-progress', label: 'În lucru', path: '/activitati/in-lucru' },
      { id: 'completed', label: 'Finalizate', path: '/activitati/finalizate' },
    ],
  },
  {
    id: 'projects',
    label: 'Proiecte',
    icon: <FolderKanban className="w-5 h-5" />,
    children: [
      { id: 'my-projects', label: 'Toate proiectele', path: '/proiecte/toate' },
      { id: 'coordinated', label: 'Proiecte coordonate', path: '/proiecte/coordonate' },
      { id: 'involved', label: 'Proiecte în lucru', path: '/proiecte/in-lucru' },
      { id: 'finished', label: 'Proiecte finalizate', path: '/proiecte/finalizate' },
    ],
  },
  {
    id: 'admin',
    label: 'Administrare',
    icon: <Shield className="w-5 h-5" />,
    adminOnly: true, // Doar pentru administratori
    children: [
      { id: 'admin-users', label: 'Utilizatori', path: '/admin/utilizatori' },
      { id: 'admin-groups', label: 'Grupuri', path: '/admin/grupuri' },
      { id: 'admin-status-activities', label: 'Status activități', path: '/admin/nomenclatoare/status-activitati' },
      { id: 'admin-lista-investitii', label: 'Lista de investiții', path: '/admin/lista-investitii' },
    ],
  },
  {
    id: 'nomenclatoare',
    label: 'Nomenclatoare',
    icon: <BookOpen className="w-5 h-5" />,
    adminOnly: true, // Doar pentru administratori
    children: [
      { id: 'nomenclatoare-status', label: 'Status activitate', path: '/admin/nomenclatoare/status' },
      {
        id: 'nomenclatoare-achizitii',
        label: 'Achiziții',
        children: [
          { id: 'nomenclatoare-doc-achizitii', label: 'Documente achiziții', path: '/admin/nomenclatoare/documente-achizitii' },
        ],
      },
      {
        id: 'nomenclatoare-livrabile',
        label: 'Livrabile',
        children: [
          { id: 'nomenclatoare-tipuri-livrabile', label: 'Tipuri livrabile', path: '/admin/nomenclatoare/tipuri-livrabile' },
          { id: 'nomenclatoare-doc-livrabile', label: 'Documente livrabile', path: '/admin/nomenclatoare/documente-livrabile' },
        ],
      },
      {
        id: 'nomenclatoare-contracte',
        label: 'Contracte',
        children: [
          { id: 'nomenclatoare-tipuri-contracte', label: 'Tipuri contracte', path: '/admin/nomenclatoare/tipuri-contracte' },
        ],
      },
      {
        id: 'nomenclatoare-implementare',
        label: 'Implementare',
        children: [
          { id: 'nomenclatoare-tipuri-implementare', label: 'Tipuri implementare', path: '/admin/nomenclatoare/tipuri-implementare' },
          { id: 'nomenclatoare-doc-implementare', label: 'Documente implementare', path: '/admin/nomenclatoare/documente-implementare' },
        ],
      },
    ],
  },
];

export function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['activities', 'projects']));
  const location = useLocation();
  const { user } = useAuth();

  // Filtrăm elementele de meniu - ascundem meniurile adminOnly dacă utilizatorul nu e admin
  const filteredNavItems = useMemo(() => {
    return navItems.filter(item => {
      if (item.adminOnly && !user?.admin) {
        return false;
      }
      return true;
    });
  }, [user?.admin]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  const isChildActive = (children?: SubNavItem[]): boolean => {
    if (!children) return false;
    return children.some(child =>
      child.path ? location.pathname === child.path :
      child.children ? isChildActive(child.children) : false
    );
  };

  const getSubIcon = (id: string) => {
    switch (id) {
      case 'my-activities':
        return <LayoutDashboard className="w-4 h-4" />;
      case 'todo':
        return <ListTodo className="w-4 h-4" />;
      case 'in-progress':
        return <PlayCircle className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'my-projects':
        return <User className="w-4 h-4" />;
      case 'coordinated':
        return <Users className="w-4 h-4" />;
      case 'involved':
        return <FolderOpen className="w-4 h-4" />;
      case 'finished':
        return <FolderCheck className="w-4 h-4" />;
      case 'admin-users':
        return <User className="w-4 h-4" />;
      case 'admin-groups':
        return <UsersRound className="w-4 h-4" />;
      case 'admin-status-activities':
        return <CircleDot className="w-4 h-4" />;
      case 'admin-status-projects':
        return <Briefcase className="w-4 h-4" />;
      case 'admin-activity-types':
        return <Layers className="w-4 h-4" />;
      case 'admin-lista-investitii':
        return <ListChecks className="w-4 h-4" />;
      case 'nomenclatoare-status':
        return <CircleDot className="w-4 h-4" />;
      case 'nomenclatoare-prioritate':
        return <Flag className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <aside
      className={clsx(
        'bg-white border-r border-gray-200 flex flex-col transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
        {!isCollapsed && (
          <div className="flex items-center gap-3">
            <img
              src="/logo-login.png"
              alt="CJ Prahova"
              className="h-10 w-auto"
            />
            <span className="font-semibold text-gray-900 text-sm leading-tight">Managementul<br/>Proiectelor</span>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          title={isCollapsed ? 'Extinde meniul' : 'Restrânge meniul'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <div key={item.id}>
            {/* Item fără copii - link direct */}
            {!item.children && item.path && (
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )
                }
                title={isCollapsed ? item.label : undefined}
              >
                <span
                  className={clsx(
                    'flex-shrink-0',
                    location.pathname === item.path
                      ? 'text-primary-600'
                      : 'text-gray-400'
                  )}
                >
                  {item.icon}
                </span>
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            )}

            {/* Item cu copii - meniu expandabil */}
            {item.children && (
              <>
                <button
                  onClick={() => !isCollapsed && toggleMenu(item.id)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors',
                    isChildActive(item.children)
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span
                    className={clsx(
                      'flex-shrink-0',
                      isChildActive(item.children)
                        ? 'text-primary-600'
                        : 'text-gray-400'
                    )}
                  >
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        className={clsx(
                          'w-4 h-4 transition-transform text-gray-400',
                          expandedMenus.has(item.id) && 'rotate-180'
                        )}
                      />
                    </>
                  )}
                </button>

                {/* Submeniu */}
                {!isCollapsed && expandedMenus.has(item.id) && (
                  <div className="mt-1 ml-4 pl-4 border-l border-gray-200 space-y-1">
                    {item.children.map((child) => (
                      child.children ? (
                        <div key={child.id}>
                          <button
                            onClick={() => toggleMenu(child.id)}
                            className={clsx(
                              'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                              isChildActive(child.children)
                                ? 'text-primary-700 font-medium'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            )}
                          >
                            <span className="flex-1 text-left">{child.label}</span>
                            <ChevronDown className={clsx('w-3.5 h-3.5 transition-transform text-gray-400', expandedMenus.has(child.id) && 'rotate-180')} />
                          </button>
                          {expandedMenus.has(child.id) && (
                            <div className="ml-3 pl-3 border-l border-gray-100 space-y-0.5">
                              {child.children.map((subChild) => (
                                <NavLink
                                  key={subChild.id}
                                  to={subChild.path!}
                                  className={({ isActive }) =>
                                    clsx(
                                      'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors',
                                      isActive
                                        ? 'bg-primary-50 text-primary-700 font-medium'
                                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                    )
                                  }
                                >
                                  <span>{subChild.label}</span>
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <NavLink
                          key={child.id}
                          to={child.path!}
                          className={({ isActive }) =>
                            clsx(
                              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                              isActive
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            )
                          }
                        >
                          <span
                            className={clsx(
                              'flex-shrink-0',
                              location.pathname === child.path
                                ? 'text-primary-500'
                                : 'text-gray-400'
                            )}
                          >
                            {getSubIcon(child.id)}
                          </span>
                          <span>{child.label}</span>
                        </NavLink>
                      )
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 py-4 px-2">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors',
              isActive
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            )
          }
          title={isCollapsed ? t('nav.settings') : undefined}
        >
          <Settings className="w-5 h-5 text-gray-400" />
          {!isCollapsed && <span>{t('nav.settings')}</span>}
        </NavLink>
      </div>
    </aside>
  );
}
